'use client';

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useController } from 'react-hook-form';
import {
  Typography,
  Input,
  Select,
  Option,
  Checkbox,
  ClickAwayListener,
} from 'src/components/PulseUI';

/**
 * @description Builds a dynamic input component
 * @returns HTMLElement
 */
interface DynamicInputProps {
  placeholder?: string;
  value?: any;
  disabled?: boolean;
  name: string;
  type: string;
  variant?: 'standard' | 'outlined' | 'filled' | undefined;
  required?: boolean;
  hidden?: boolean;
  options?: Option[];
  min?: number;
  max?: number;
  read_only?: boolean;
  control: any;
  rules?: any;
  label?: string;
  size?: 'small' | 'medium';
}

interface Option {
  value?: string;
  label: string;
  code?: string;
}

// Map MUI variant to PulseUI variant
const mapVariant = (variant?: string) => {
  switch (variant) {
    case 'standard':
      return 'plain';
    case 'outlined':
      return 'outlined';
    case 'filled':
      return 'soft';
    default:
      return 'plain';
  }
};

// Map MUI size to PulseUI size
const mapSize = (size?: string) => {
  switch (size) {
    case 'small':
      return 'sm';
    case 'medium':
      return 'md';
    default:
      return 'sm';
  }
};

// Custom Combobox component for string_with_suggestions type
const Combobox = ({
  value,
  onChange,
  options = [],
  placeholder,
  variant,
  disabled,
}: {
  value: any;
  onChange: (value: any) => void;
  options: Option[];
  placeholder?: string;
  variant?: 'plain' | 'outlined' | 'soft';
  disabled?: boolean;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef<HTMLDivElement>(null);

  // Find the selected option label
  const selectedOption = options?.find(
    (item) => item?.value === value || item?.code === value
  );

  const filteredOptions = inputValue
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(inputValue.toLowerCase())
      )
    : options;

  useEffect(() => {
    if (selectedOption) {
      setInputValue(selectedOption.label);
    }
  }, [selectedOption]);

  const updatePosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  const handleOpen = () => {
    if (!disabled) {
      updatePosition();
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    // Reset input to selected value if empty
    if (!inputValue && selectedOption) {
      setInputValue(selectedOption.label);
    }
  };

  const handleSelect = (option: Option) => {
    onChange(option?.code || option?.value || null);
    setInputValue(option.label);
    setIsOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div className="relative w-full" data-testid="dynamic-combobox">
        <div ref={inputRef}>
          <Input
            fullWidth
            variant={variant}
            placeholder={placeholder || 'Select...'}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (!isOpen) handleOpen();
            }}
            onFocus={handleOpen}
            onClick={handleOpen}
            disabled={disabled}
            aria-label={placeholder}
            data-testid="dynamic-combobox-input"
          />
        </div>

        {isOpen &&
          createPortal(
            <div
              className="fixed bg-white shadow-lg rounded-lg border border-neutral-200 max-h-[200px] overflow-auto z-[9999]"
              style={{
                width: position.width,
                top: position.top,
                left: position.left,
              }}
              data-portal-content="true"
              data-testid="dynamic-combobox-dropdown"
            >
              {filteredOptions.length > 0 ? (
                <div className="flex flex-col py-1" role="listbox">
                  {filteredOptions.map((option, index) => (
                    <button
                      type="button"
                      key={index}
                      onClick={() => handleSelect(option)}
                      className={`py-2 px-3 text-left text-sm hover:bg-neutral-100 cursor-pointer ${
                        (option?.value === value || option?.code === value)
                          ? 'bg-neutral-50'
                          : ''
                      }`}
                      role="option"
                      aria-selected={option?.value === value || option?.code === value}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="py-2 px-3 text-sm text-neutral-500">
                  No options
                </div>
              )}
            </div>,
            document.body
          )}
      </div>
    </ClickAwayListener>
  );
};

const DynamicInput = ({ ...props }: DynamicInputProps) => {
  const { field } = useController({
    name: props.name,
    control: props.control,
    rules: {
      ...props.rules,
    },
    defaultValue: props.value,
  });

  const pulseVariant = mapVariant(props.variant);
  const pulseSize = mapSize(props.size);

  switch (props.type) {
    case 'string':
      return (
        <Input
          fullWidth
          variant={pulseVariant}
          size={pulseSize}
          placeholder={props.placeholder}
          disabled={props.disabled}
          required={props.required}
          {...field}
          value={field.value || ''}
          aria-label={props.label || props.name}
          data-testid={`dynamic-input-${props.name}`}
        />
      );

    case 'integer':
      return (
        <Input
          fullWidth
          variant={pulseVariant}
          size={pulseSize}
          type="number"
          placeholder={props.placeholder}
          disabled={props.disabled}
          required={props.required}
          min={props?.min || 0}
          max={props?.max || undefined}
          {...field}
          value={field?.value || 0}
          onChange={(e) => field.onChange(Number(e.target.value))}
          aria-label={props.label || props.name}
          data-testid={`dynamic-input-${props.name}`}
        />
      );

    case 'selector': {
      return (
        <div data-testid={`dynamic-select-${props.name}`}>
          <Select
            variant={pulseVariant}
            size={pulseSize}
            placeholder="Select..."
            disabled={props.disabled}
            value={field.value || ''}
            onChange={(_, newValue) => field.onChange(newValue)}
            aria-label={props.label || props.name}
          >
            {props?.options?.map((option, index) => (
              <Option key={index} value={option.value}>
                {option?.label}
              </Option>
            ))}
          </Select>
        </div>
      );
    }

    case 'string_with_suggestions': {
      return (
        <Combobox
          value={field.value}
          onChange={(newValue) => field.onChange(newValue)}
          options={props?.options || []}
          placeholder={props.placeholder}
          variant={pulseVariant}
          disabled={props.disabled}
        />
      );
    }

    case 'date':
      return (
        <Input
          fullWidth
          variant={pulseVariant}
          size={pulseSize}
          type="date"
          placeholder={props.placeholder}
          disabled={props.disabled}
          required={props.required}
          {...field}
          value={field.value || ''}
          aria-label={props.label || props.name}
          data-testid={`dynamic-input-${props.name}`}
        />
      );

    case 'boolean':
      return (
        <div
          className="flex items-center gap-2"
          data-testid={`dynamic-checkbox-${props.name}`}
        >
          <Checkbox
            checked={field.value || false}
            onChange={(e) => field.onChange(e.target.checked)}
            disabled={props.disabled || false}
            size={pulseSize}
            label={props.label}
            aria-label={props.label || props.name}
          />
        </div>
      );

    default:
      return (
        <Typography variant="body1" component="span" aria-label="default case">
          {props.value}
        </Typography>
      );
  }
};

export default DynamicInput;
